import axios from 'axios';

const API_URL = 'https://lmssummarizer-edar.onrender.com';
const CHAT_API_URL = 'https://lms5stg.wpenginepowered.com/wp-json/mwai/v1/simpleChatbotQuery';

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create axios instance for chat API
const chatApi = axios.create({
  baseURL: CHAT_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer NysoraForApps`,
  },
});

// Custom event for unauthorized responses
export const AUTH_EVENTS = {
  UNAUTHORIZED: 'auth:unauthorized',
};

// Create a custom event emitter for handling auth events
export const emitAuthEvent = (eventType) => {
  const event = new CustomEvent(eventType);
  window.dispatchEvent(event);
};

// Add token to requests if it exists
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add external API token to chat requests
chatApi.interceptors.request.use(
  (config) => {
    const externalToken = process.env.REACT_APP_EXTERNAL_API_TOKEN;
    if (externalToken) {
      config.headers.Authorization = `Bearer ${externalToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if error is unauthorized (401)
    if (error.response && error.response.status === 401) {
      // Emit unauthorized event
      emitAuthEvent(AUTH_EVENTS.UNAUTHORIZED);
    }
    return Promise.reject(error);
  }
);

export const loginUser = async (username, password) => {
  try {
    const response = await api.post('/login', { username, password });
    return response.data;
  } catch (error) {
    throw new Error('Invalid username or password');
  }
};

export const validateToken = async () => {
  try {
    // Use a simple endpoint to validate the token
    // If there's no specific validation endpoint, you can use any endpoint that requires authentication
    const response = await api.post('/validate-token');
    return response.data;
  } catch (error) {
    throw new Error('Token validation failed');
  }
};

export const sendChatMessage = async (prompt, botId, chatId) => {
  try {
    const response = await api.post('/chat-message', {
      prompt,
      botId,
      chatId: String(chatId),
    });
    return response.data;
  } catch (error) {
    // We don't need to handle 401 here as it's caught by the interceptor
    throw new Error(error.response?.data?.message || 'Could not reach backend');
  }
};

export const sendHeroChatMessage = async (prompt, botId, chatId) => {
  try {
    const response = await chatApi.post('', {
      prompt: String(prompt),
      botId: String(botId),
      chatId: String(chatId),
    });
    return response.data;
  } catch (error) {
    // We don't need to handle 401 here as it's caught by the interceptor
    throw new Error(error.response?.data?.message || 'Could not reach backend');
  }
};

// Stream chat response from AI Engine via server-sent style chunks
// onStart(): called once stream starts
// onChunk(chunk): called for each text chunk
// onComplete(finalText): called when done
// onError(errMessage): called on error
export const streamChatMessage = ({ botId, messages, onStart, onChunk, onComplete, onError }) => {
  const AI_ENGINE_URL = 'https://ai-engine-jm7d.onrender.com/api/chat/stream';
  const apiKey = process.env.REACT_APP_AI_ENGINE_API_KEY || 'nysora_ai_wq7QOc5yDerWwvFSf5w42i4xfwkU0tSJdFxMNVQ9U6Gk41R8';

  try {
    const xhr = new XMLHttpRequest();
    let lastIndex = 0;
    let streamedMessage = '';

    xhr.open('POST', AI_ENGINE_URL);
    xhr.setRequestHeader('Content-Type', 'application/json');
    if (apiKey) {
      xhr.setRequestHeader('X-API-Key', apiKey);
    }

    if (typeof onStart === 'function') onStart();

    xhr.onprogress = () => {
      const chunk = xhr.responseText.substring(lastIndex);
      lastIndex = xhr.responseText.length;
      const lines = chunk.split('\n');
      lines.forEach(line => {
        if (!line.startsWith('data:')) return;
        let data = line.slice(5);
        if (data === '[DONE]' || data === 'ok') return;
        if ((data.startsWith('"') && data.endsWith('"')) || (data.startsWith("'") && data.endsWith("'"))) {
          data = data.slice(1, -1);
        }
        try {
          const parsed = JSON.parse(data);
          if (parsed.content) {
            streamedMessage += parsed.content;
            if (typeof onChunk === 'function') onChunk(parsed.content, streamedMessage);
          }
        } catch (e) {
          // swallow parse errors per line but report via onError in debug
          // console.error('Failed to parse SSE JSON:', data, e);
        }
      });
    };

    xhr.onload = () => {
      if (typeof onComplete === 'function') onComplete(streamedMessage);
    };

    xhr.onerror = () => {
      const msg = 'Error receiving response. Please try again.';
      if (typeof onError === 'function') onError(msg);
    };

    xhr.send(
      JSON.stringify({
        bot: String(botId),
        messages,
      })
    );

    return () => {
      try { xhr.abort(); } catch (_) {}
    };
  } catch (err) {
    if (typeof onError === 'function') onError('Failed to initiate stream');
    return () => {};
  }
};

export default api; 